import { create } from 'zustand';
import type { HMIScreen, HMIComponent } from '@/hmi/model/types';
import { useProjectStore } from '@/store/projectStore';

export interface HMIEditorState {
  activeScreenId: string | null;
  selectedComponentIds: string[];
  zoom: number;
  panX: number;
  panY: number;

  // Actions
  setActiveScreen: (id: string) => void;
  setSelectedComponents: (ids: string[]) => void;
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  
  // Project commands (writes to projectStore)
  addScreen: (name: string) => void;
  addComponent: (component: Omit<HMIComponent, 'id'>) => void;
  updateComponent: (id: string, updates: Partial<HMIComponent>) => void;
  removeComponent: (id: string) => void;
}

export const useHMIEditorStore = create<HMIEditorState>()((set, get) => ({
  activeScreenId: null,
  selectedComponentIds: [],
  zoom: 1,
  panX: 0,
  panY: 0,

  setActiveScreen: (id) => set({ activeScreenId: id, selectedComponentIds: [] }),
  setSelectedComponents: (ids) => set({ selectedComponentIds: ids }),
  setZoom: (zoom) => set({ zoom }),
  setPan: (panX, panY) => set({ panX, panY }),

  addScreen: (name) => {
    const projectState = useProjectStore.getState();
    const newScreen: HMIScreen = {
      id: `screen_${Date.now()}`,
      name,
      width: 800,
      height: 600,
      backgroundColor: '#ffffff',
      components: []
    };
    
    projectState.setHmiProject({
      ...projectState.project.hmi,
      screens: [...projectState.project.hmi.screens, newScreen],
      defaultScreenId: projectState.project.hmi.screens.length === 0 ? newScreen.id : projectState.project.hmi.defaultScreenId
    });
    set({ activeScreenId: newScreen.id });
  },

  addComponent: (compWithoutId) => {
    const projectState = useProjectStore.getState();
    const activeScreenId = get().activeScreenId || projectState.project.hmi.defaultScreenId;
    if (!activeScreenId) return;

    const newComponent: HMIComponent = {
      ...compWithoutId,
      id: `comp_${Date.now()}_${Math.floor(Math.random()*1000)}`
    };

    const newScreens = projectState.project.hmi.screens.map(s => 
      s.id === activeScreenId 
        ? { ...s, components: [...s.components, newComponent] }
        : s
    );

    projectState.setHmiProject({ ...projectState.project.hmi, screens: newScreens });
    set({ selectedComponentIds: [newComponent.id] });
  },

  updateComponent: (id, updates) => {
    const projectState = useProjectStore.getState();
    const activeScreenId = get().activeScreenId;
    if (!activeScreenId) return;

    const newScreens = projectState.project.hmi.screens.map(s => {
      if (s.id !== activeScreenId) return s;
      return {
        ...s,
        components: s.components.map(c => c.id === id ? { ...c, ...updates } : c)
      };
    });

    projectState.setHmiProject({ ...projectState.project.hmi, screens: newScreens });
  },

  removeComponent: (id) => {
    const projectState = useProjectStore.getState();
    const activeScreenId = get().activeScreenId;
    if (!activeScreenId) return;

    const newScreens = projectState.project.hmi.screens.map(s => {
      if (s.id !== activeScreenId) return s;
      return {
        ...s,
        components: s.components.filter(c => c.id !== id)
      };
    });

    projectState.setHmiProject({ ...projectState.project.hmi, screens: newScreens });
    set((state) => ({
      selectedComponentIds: state.selectedComponentIds.filter(compId => compId !== id)
    }));
  }
}));
